import type { QueryObserverResult } from "@tanstack/react-query";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useContext, useState } from "react";

import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import {
	contactDetailSchema,
	creditLimitSchema,
	customerDetailsSchema,
	freightCustomerSchema,
} from "~/shared/schemas/customer-schema.ts";
import { holidaySchema } from "~/shared/schemas/holiday-schema.ts";
import type {
	QuoteResponse,
	QuoteShipmentBody,
} from "~/shared/schemas/quote-schema.ts";
import {
	bookTenderResponseSchema,
	quoteResponseSchema,
	quoteShipmentBodySchema,
} from "~/shared/schemas/quote-schema.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import {
	getAndValidate,
	putAndValidate,
} from "~/shared/services/api/fetch-client.ts";
import type { ApiError } from "~/shared/types/api-types.ts";
import type { ShipmentSummaryPageLoadData } from "~/shared/types/hooks-types.ts";

import { useQuoteAddressContext } from "../context/quote-address-context.tsx";
import { QuoteDetailsContext } from "../context/quote-details-context.tsx";
import {
	formatQuoteForTender,
	formatQuoteForUpdate,
} from "../utils/format-quote-state.ts";
import {
	createLtlMutationErrorHandler,
	getLtlFallbackMessage,
} from "../utils/ltl-error-handler.ts";

type TenderQuoteRequest = Pick<QuoteResponse, "id" | "eTag">;
type TenderQuoteInput = QuoteResponse | TenderQuoteRequest;

const isFullQuoteResponse = (
	quoteData: TenderQuoteInput,
): quoteData is QuoteResponse => "origin" in quoteData;

// Re-export shared LTL error helpers so existing consumers (and test mocks)
// can keep importing them from this module.
export {
	QUOTE_REFRESH_REQUIRED_STATUS,
	TENDER_ERROR_MESSAGE,
} from "../utils/ltl-error-handler.ts";

export const useShipmentSummaryPageLoadQueries = (
	customerId: number,
	brokerId: number,
): QueryObserverResult<ShipmentSummaryPageLoadData> => {
	return useQuery<ShipmentSummaryPageLoadData>({
		queryKey: ["shipmentSummaryPageLoad", customerId, brokerId],
		queryFn: async (): Promise<ShipmentSummaryPageLoadData> => {
			try {
				const [
					customerDataResult,
					creditDataResult,
					contactDetailDataResult,
					freightCustomerDataResult,
					holidayDataResult,
				] = await Promise.all([
					getAndValidate(
						`${apiRoutes.SELECTCUSTOMER}${customerId}`,
						customerDetailsSchema,
					),
					getAndValidate(
						`${apiRoutes.CREDITINFO}${customerId}`,
						creditLimitSchema,
					),
					getAndValidate(
						`${apiRoutes.CONTACTINFO.replace(":brokerId", encodeURIComponent(brokerId))}`,
						contactDetailSchema,
					),
					getAndValidate(
						`${apiRoutes.FREIGHTCUSTOMER.replace(":customerId", encodeURIComponent(customerId))}`,
						freightCustomerSchema.array(),
					),
					getAndValidate(`${apiRoutes.HOLIDAYS}false`, holidaySchema.array()),
				]);
				return {
					customerData: customerDataResult.success
						? customerDataResult.data
						: {
								id: 0,
								name: "",
								hasSignedLTLAddendum: false,
								status: { id: 0, name: "" },
								primaryBroker: { id: 0, name: "", email: "" },
								ltlBroker: null,
							},
					creditData: creditDataResult.success
						? creditDataResult.data
						: { creditLimit: 0, availableCredit: 0, totalBalance: 0 },
					contactData: contactDetailDataResult.success
						? contactDetailDataResult.data
						: { userId: 0, primaryExtension: null },
					freightCustomer: freightCustomerDataResult.success
						? freightCustomerDataResult.data
						: [],
					holidays: holidayDataResult.success ? holidayDataResult.data : [],
				};
			} catch (error) {
				// Log the error for debugging
				console.error("Carrier selection page load failed:", error);
				// Return fallback data to prevent app crash
				return {
					customerData: {
						id: 0,
						name: "",
						hasSignedLTLAddendum: false,
						status: { id: 0, name: "" },
						primaryBroker: { id: 0, name: "", email: "" },
						ltlBroker: null,
					},
					creditData: { creditLimit: 0, availableCredit: 0, totalBalance: 0 },
					contactData: { userId: 0, primaryExtension: null },
					freightCustomer: [],
					holidays: [],
				};
			}
		},
		staleTime: 1000 * 60 * 10, // 10 minutes
		retry: 5,
		refetchOnWindowFocus: false,
		refetchOnMount: false,
		refetchOnReconnect: false,
	});
};

export const useUpdateQuote = () => {
	const { isInternal } = useDeploymentType();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (body: QuoteShipmentBody) => {
			const url = isInternal
				? apiRoutes.UPDATEQUOTE.replace(":quoteId", String(body.quoteId))
				: apiRoutes.UPDATECUSTOMERQUOTE.replace(
						":quoteId",
						String(body.quoteId),
					);
			return await putAndValidate(url, body, quoteResponseSchema, {
				overrideHook: (request) => {
					request.headers.set("If-Match", body.eTag ?? "");
				},
				onError: createLtlMutationErrorHandler(
					getLtlFallbackMessage(isInternal),
				),
			});
		},
		onSuccess: (data, variables) => {
			// Keep the cached quote in sync with the saved shipment details.
			// The carrier-selection step seeds ["getQuote", quoteId] (via
			// useSaveSelectedCarrier) before shipment details are entered, so
			// without this the Quote Board -> shipment-details navigation would
			// render the stale (blank) cached quote until a full page refresh.
			queryClient.invalidateQueries({
				queryKey: ["getQuote", variables.quoteId],
			});
			if (data.success) {
				queryClient.setQueryData(["getQuote", variables.quoteId], data.data);
			}
		},
	});
};
export const useSubmitUpdatedQuote = () => {
	const { quoteState } = useContext(QuoteDetailsContext);
	const { selectedAddressBookItems } = useQuoteAddressContext();
	const { mutateAsync, isPending } = useUpdateQuote();
	const submitUpdatedQuote = async (quoteStateOverride = quoteState) => {
		// format body with util fn
		const body = formatQuoteForUpdate(
			quoteStateOverride,
			selectedAddressBookItems,
		);
		const parsedJson = quoteShipmentBodySchema.safeParse(body);

		if (parsedJson.success) {
			// save quote
			const result = await mutateAsync(parsedJson.data);
			return result.success
				? { status: 200, data: result.data }
				: { status: result.status ?? 400, message: result.error };
		} else {
			return {
				status: 400,
				message:
					"We encountered an issue with the shipment information. Please review your details and try again.",
			};
		}
	};
	return { submitUpdatedQuote, isLoading: isPending };
};
export const useTenderQuote = () => {
	const { isInternal } = useDeploymentType();
	return useMutation({
		mutationFn: async (quoteBody: TenderQuoteRequest) => {
			const url = isInternal
				? apiRoutes.TENDERQUOTE.replace(":quoteId", String(quoteBody.id))
				: apiRoutes.TENDERCUSTOMERQUOTE.replace(
						":quoteId",
						String(quoteBody.id),
					);
			return await putAndValidate(url, null, quoteResponseSchema, {
				overrideHook: (request) => {
					request.headers.set("If-Match", String(quoteBody.eTag));
				},
				onError: createLtlMutationErrorHandler(
					getLtlFallbackMessage(isInternal),
				),
			});
		},
	});
};
export const useSubmitTenderQuote = () => {
	const { mutateAsync, isPending } = useTenderQuote();
	const { mergeResponseIntoQuoteState } = useContext(QuoteDetailsContext);
	const submitTenderQuote = async (quoteData: TenderQuoteInput) => {
		const body: TenderQuoteRequest = isFullQuoteResponse(quoteData)
			? formatQuoteForTender(quoteData)
			: quoteData;

		const result = await mutateAsync(body);
		if (!result.success) {
			return { status: result.status ?? 400, message: result.error };
		}

		mergeResponseIntoQuoteState({
			id: result.data.id,
			carrierPrices: result.data.carrierPrices,
			eTag: result.data.eTag,
			createdDate: result.data.createdDate,
			expirationDate: result.data.expirationDate,
			poNumber: result.data.poNumber ?? undefined,
		});

		return { status: 200, data: result.data };
	};
	return { submitTenderQuote, isLoading: isPending };
};

type BookTenderShipmentRequest = {
	quoteId: number;
	eTag: string;
	selectedCarrierPriceId: number;
};

export const useBookTenderShipment = () => {
	const { isInternal } = useDeploymentType();
	return useMutation({
		mutationFn: async (body: BookTenderShipmentRequest) => {
			const url = (
				isInternal
					? apiRoutes.BOOKTENDERSHIPMENT
					: apiRoutes.BOOKTENDERCUSTOMERSHIPMENT
			).replace(":quoteId", String(body.quoteId));
			return await putAndValidate(
				url,
				{ selectedCarrierPriceId: body.selectedCarrierPriceId },
				bookTenderResponseSchema,
				{
					overrideHook: (request) => {
						request.headers.set("If-Match", body.eTag ?? "");
					},
					onError: createLtlMutationErrorHandler(
						getLtlFallbackMessage(isInternal),
					),
				},
			);
		},
	});
};

export const useSubmitBookTenderShipment = () => {
	const { mutateAsync, isPending } = useBookTenderShipment();
	const { mergeResponseIntoQuoteState } = useContext(QuoteDetailsContext);
	const submitBookTenderShipment = async (body: BookTenderShipmentRequest) => {
		const result = await mutateAsync(body);
		if (!result.success) {
			return { status: result.status ?? 400, message: result.error };
		}

		mergeResponseIntoQuoteState({
			id: result.data.id,
			carrierPrices: result.data.carrierPrices,
			eTag: result.data.eTag,
			createdDate: result.data.createdDate,
			expirationDate: result.data.expirationDate,
			poNumber: result.data.poNumber ?? undefined,
		});

		return { status: 200, data: result.data };
	};
	return { submitBookTenderShipment, isLoading: isPending };
};

/**
 * Mirrors the legacy "Refresh Quote" confirmation flow. When the LTL update
 * or tender call returns 412 (eTag conflict), the page asks the user whether
 * to reload the latest quote from the server. On confirm, this hook fetches
 * the quote again and rehydrates `QuoteDetailsContext` via
 * `loadQuoteFromResponse`, so subsequent retries use the fresh eTag.
 */
export const useRefreshConflictedQuote = () => {
	const { isInternal } = useDeploymentType();
	const { loadQuoteFromResponse } = useContext(QuoteDetailsContext);
	const [isRefreshing, setIsRefreshing] = useState(false);

	const refreshQuote = useCallback(
		async (quoteId: number): Promise<ApiError | { success: true }> => {
			if (!quoteId) {
				return { success: false, error: getLtlFallbackMessage(isInternal) };
			}
			setIsRefreshing(true);
			try {
				const url = isInternal
					? `${apiRoutes.GETQUOTE}/${quoteId}`
					: `${apiRoutes.GETCUSTOMERQUOTE}/${quoteId}`;
				const result = await getAndValidate(url, quoteResponseSchema);
				if (!result.success) {
					return result;
				}
				loadQuoteFromResponse(result.data);
				return { success: true };
			} finally {
				setIsRefreshing(false);
			}
		},
		[isInternal, loadQuoteFromResponse],
	);

	return { refreshQuote, isRefreshing };
};
